import BigNumber from 'bignumber.js';
import { WithholdingTaxType } from '~/types/document';
import { SocialSecurityForm, WithholdingTaxForm } from '~/types/document-builder';
import { TipoCassa } from '~/types/invoicing';
import { socialSecurityTotals } from '~/utils/invoicing/social-security-totals';

/**
 * Return the value that can be used to withold based on social security type.
 * @param netPrice -> the net price of the invoice.
 * @param socialSecurity -> the social security added on the invoice could be null.
 * @param maxVat -> percentage of the netPrice on which the withholding is to be calculated
 */

export const withholdingTaxable = (
  netTotal: BigNumber,
  socialSecurity: SocialSecurityForm | null,
  maxVat: string,
): BigNumber => {
  if (socialSecurity?.type === TipoCassa.INPS) {
    const socialSecurityTotal = socialSecurityTotals(socialSecurity, maxVat);
    return BigNumber.sum(netTotal, socialSecurityTotal?.amount ?? 0);
  }
  return netTotal;
};

/**
 * Calculate the amount of the withholding.
 * @param withholding -> the withholding values from the form
 */
export const calcWithholdingAmount = (withholding: WithholdingTaxForm) => {
  const taxable = BigNumber(withholding.taxableAmount);
  const percentage = BigNumber(withholding.percentage);
  const amount = taxable.multipliedBy(percentage).dividedBy(100);

  return amount.decimalPlaces(2).toFixed();
};

export const withholdingTypeOptions = [
  { value: WithholdingTaxType.PersoneFisiche, label: 'Ritenuta persone fisiche' },
  { value: WithholdingTaxType.PersoneGiuridiche, label: 'Ritenuta persone giuridiche' },
  { value: WithholdingTaxType.ContributoINPS, label: 'Contributo INPS' },
  { value: WithholdingTaxType.ContributoENASARCO, label: 'Contributo ENASARCO' },
  { value: WithholdingTaxType.ContributoENPAM, label: 'Contributo ENPAM' },
  { value: WithholdingTaxType.AltroContributo, label: 'Altro contributo previdenziale' },
];

interface WithholdingMotivationOption {
  value: string;
  label: string;
}

export const withholdingMotivationOptions: WithholdingMotivationOption[] = [
  { value: 'A', label: 'Prestazioni di lavoro autonomo rientranti nell’esercizio di arte o professione abituale.' },
  {
    value: 'B',
    label:
      'Utilizzazione economica, da parte dell’autore o dell’inventore, di opere dell’ingegno, di brevetti industriali e di processi, relativi a esperienze acquisite in campo industriale, commerciale o scientifico.',
  },
  {
    value: 'C',
    label:
      'Utili derivanti da contratti di associazione in partecipazione e da contratti di cointeressenza, quando l’apporto è costituito esclusivamente dalla prestazione di lavoro.',
  },
  { value: 'D', label: 'Utili spettanti ai soci promotori e ai soci fondatori delle società di capitali.' },
  { value: 'E', label: 'Levata di protesti cambiari da parte dei segretari comunali.' },
  { value: 'G', label: 'Indennità corrisposte per la cessazione di attività sportiva professionale.' },
  {
    value: 'H',
    label:
      'Indennità corrisposte per la cessazione dei rapporti di agenzia delle persone fisiche e delle società di persone, con esclusione delle somme maturate entro il 31.12.2003, già imputate per competenza.',
  },
  { value: 'I', label: 'Indennità corrisposte per la cessazione da funzioni notarili.' },
  {
    value: 'J',
    label:
      'Compensi corrisposti ai raccoglitori occasionali di tartufi non identificati ai fini dell’imposta sul valore',
  },
  { value: 'K', label: 'Assegni di servizio civile di cui all’art. 16 del D.lgs. n. 40 del 6 marzo 2017' },
  {
    value: 'L',
    label:
      'Utilizzazione economica, da parte di soggetto diverso dall’autore o dall’inventore, di opere dell’ingegno, di brevetti industriali e di processi, formule e informazioni relative a esperienze acquisite.',
  },
  {
    value: 'L1',
    label:
      'Redditi derivanti dall’utilizzazione economica di opere dell’ingegno, di brevetti industriali e di processi, che sono percepiti da soggetti che abbiano acquistato a titolo oneroso i diritti alla loro utilizzazione.',
  },
  {
    value: 'M',
    label: 'Prestazioni di lavoro autonomo non esercitate abitualmente, obblighi di fare, di non fare o permettere.',
  },
  { value: 'M1', label: 'Redditi derivanti dall’assunzione di obblighi di fare, di non fare o permettere.' },
  {
    value: 'M2',
    label:
      'Prestazioni di lavoro autonomo non esercitate abitualmente per le quali sussiste l’obbligo di iscrizione alla Gestione Separata ENPAPI.',
  },
  {
    value: 'N',
    label:
      'Indennità di trasferta, rimborso forfettario di spese, premi e compensi erogati: .. nell’esercizio diretto di attività sportive dilettantistiche',
  },
  {
    value: 'O',
    label:
      'Prestazioni di lavoro autonomo non esercitate abitualmente, obblighi di fare, di non fare o permettere, per le quali non sussiste l’obbligo di iscrizione alla gestione separata (Circ. Inps 104/2001).',
  },
  {
    value: 'O1',
    label:
      'Redditi derivanti dall’assunzione di obblighi di fare, di non fare o permettere, per le quali non sussiste l’obbligo di iscrizione alla gestione separata (Circ. INPS n. 104/2001).',
  },
  {
    value: 'P',
    label:
      'Compensi corrisposti a soggetti non residenti privi di stabile organizzazione per l’uso o la concessione in uso di attrezzature industriali, commerciali o scientifiche che si trovano nel territorio dello Stato',
  },
  { value: 'Q', label: 'Provvigioni corrisposte ad agente o rappresentante di commercio monomandatario.' },
  { value: 'R', label: 'Provvigioni corrisposte ad agente o rappresentante di commercio plurimandatario.' },
  { value: 'S', label: 'Provvigioni corrisposte a commissionario.' },
  { value: 'T', label: 'Provvigioni corrisposte a mediatore.' },
  { value: 'U', label: 'Provvigioni corrisposte a procacciatore di affari.' },
  {
    value: 'V',
    label:
      'Provvigioni corrisposte a incaricato per le vendite a domicilio e provvigioni corrisposte a incaricato per la vendita porta a porta e per la vendita ambulante di giornali quotidiani e periodici (L. 25.02.1987, n. 67).',
  },
  {
    value: 'V1',
    label:
      'Redditi derivanti da attività commerciali non esercitate abitualmente (ad esempio, provvigioni corrisposte per prestazioni occasionali ad agente o rappresentante di commercio, mediatore, procacciatore d’affari);',
  },
  {
    value: 'V2',
    label:
      'Redditi derivanti dalle prestazioni non esercitate abitualmente rese dagli incaricati alla vendita diretta a domicilio;',
  },
  {
    value: 'W',
    label:
      'Corrispettivi erogati per prestazioni relative a contratti d’appalto cui si sono resi applicabili le disposizioni contenute nell’art. 25-ter D.P.R. 600/1973.',
  },
  {
    value: 'X',
    label:
      'Canoni corrisposti nel 2004 da società o enti residenti, ovvero da stabili organizzazioni di società estere di cui all’art. 26-quater, c. 1, lett. a) e b) D.P.R. 600/1973.',
  },
  { value: 'Y', label: 'Canoni corrisposti dal 1.01.2005 al 26.07.2005 da soggetti di cui al punto precedente.' },
  { value: 'ZO', label: 'Titolo diverso dai precedenti.' },
];
